/* eslint-disable max-len */
import { FilterAndSortField } from '@1po/1po-bff-fe-spec/generated/aftersales_order/request/model/FilterAndSortField';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/basket/request/RemoveReferences';
import {
  ClaimItem,
  CreateOrderClaimRequest,
} from '@1po/1po-bff-fe-spec/generated/claim/request/CreateOrderClaimRequest';
import { FilterAndSortField as ClaimsFilterAndSortField } from '@1po/1po-bff-fe-spec/generated/claim/request/model/FilterAndSortField';
import { FilterArray } from '@1po/1po-bff-fe-spec/generated/common/filter_and_sort/FilterArray';
import { FilterBy } from '@1po/1po-bff-fe-spec/generated/common/filter_and_sort/FilterBy';
import { DepositReturnListLine } from '@1po/1po-bff-fe-spec/generated/deposit_return_list/model/DepositReturnListLine';
import { ExadisReturnListLine } from '@1po/1po-bff-fe-spec/generated/exadis_return_list/model/ExadisReturnListLine';
import { FilterAndSortField as MyClientsOrderFilterAndSortField } from '@1po/1po-bff-fe-spec/generated/my_clients_order/request/model/FilterAndSortField';
import {
  CheckoutExternalBasketReference,
  CheckoutOtherSection,
  CheckoutVehicleReference,
  OrderMKTPDeliveryMode,
  ReferenceWithDelivery,
} from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { isRefMKTP } from 'domains/basket/Basket.store';
import {
  AscOrderFilterAndSortFieldLocal,
  AscOrdersFilterAndSortField,
  ClaimForm,
  ClaimLabourInfo,
  ClaimsFilterAndSortFieldLocal,
  ClaimsFilterFields,
  DATE_FROM,
  DATE_TO,
  FULL_OTC,
  MyClientsOrderFilterAndSortFieldLocal,
  MyClientsOrdersFilterAndSortField,
} from 'domains/order/Order.types';
import { getFormGroupType } from 'domains/order/Order.utils';
import {
  ValidationItemsExternal,
  ValidationItemsOthers,
  ValidationItemsVehicles,
} from 'domains/orderValidation/OrderValidation.types';
import { FilterOperator } from 'utils/types';
import { ExternalBasketSectionLocal, ReferenceLocal, VehicleLocal } from '../basket/Basket.types';

const hasValidReferenceNumber = (referenceNumber: string | undefined): referenceNumber is string =>
  typeof referenceNumber === 'string' && referenceNumber.trim().length > 0;

export const collectCheckoutVehicleReference = (
  vehicles: VehicleLocal[],
  vehiclesValidation: ValidationItemsVehicles[] | undefined,
  mktpDeliveryMode?: OrderMKTPDeliveryMode,
): CheckoutVehicleReference[] => {
  return vehicles.reduce((acc: CheckoutVehicleReference[], vehicle: VehicleLocal) => {
    const vehicleValidation = vehiclesValidation?.find((veh) => veh.vehicleKey === vehicle.vehicleDetail.vehicleKey);
    const enableMKTPDeliveryMode = vehicle.references.find((r) => isRefMKTP(r)) !== undefined;
    return [
      ...acc,
      {
        vehicleKey: vehicle.vehicleDetail.vehicleKey,
        references: collectReferenceNumbersWithDelivery(vehicle.references ? vehicle.references : []),
        paymentClientCode: vehicleValidation?.paymentMethod,
        defaultDeliveryType: vehicleValidation?.deliveryAddress,
        instruction: vehicleValidation?.instructions,
        mktpDeliveryMode: enableMKTPDeliveryMode ? mktpDeliveryMode : undefined,
      },
    ];
  }, []);
};

export const collectCheckoutExternalReference = (
  externalBaskets: ExternalBasketSectionLocal[],
  externalValidation: ValidationItemsExternal[] | undefined,
  mktpDeliveryMode?: OrderMKTPDeliveryMode,
): CheckoutExternalBasketReference[] => {
  return externalBaskets.reduce((acc: CheckoutExternalBasketReference[], ext: ExternalBasketSectionLocal) => {
    const externalBasketValidation = externalValidation?.find(
      (external) => external.externalBasketId === ext.externalBasketId,
    );
    const enableMKTPDeliveryMode = ext.references.find((r) => isRefMKTP(r)) !== undefined;
    return [
      ...acc,
      {
        externalBasketId: ext.externalBasketId,
        references: collectReferenceNumbersWithDelivery(ext.references),
        defaultDeliveryType: externalBasketValidation?.deliveryAddress,
        paymentClientCode: externalBasketValidation?.paymentMethod,
        instruction: externalBasketValidation?.instructions,
        mktpDeliveryMode: enableMKTPDeliveryMode ? mktpDeliveryMode : undefined,
      },
    ];
  }, []);
};

export const collectCheckoutOtherSection = (
  references: ReferenceLocal[],
  orderValidation: ValidationItemsOthers | undefined,
  mktpDeliveryMode?: OrderMKTPDeliveryMode,
): CheckoutOtherSection => {
  const enableMKTPDeliveryMode = references.find((r) => isRefMKTP(r)) !== undefined;
  return {
    references: collectReferenceNumbersWithDelivery(references),
    paymentClientCode: orderValidation?.paymentMethod,
    defaultDeliveryType: orderValidation?.deliveryAddress,
    instruction: orderValidation?.instructions,
    mktpDeliveryMode: enableMKTPDeliveryMode ? mktpDeliveryMode : undefined,
  };
};
// for external basket where sellerNumber is not supported, we need only referenceNumber
export const collectReferenceNumbers = (references: ReferenceLocal[]): string[] => {
  return references.reduce((acc: string[], ref: ReferenceLocal) => {
    if (ref.isSelected && hasValidReferenceNumber(ref.referenceNumber)) {
      return [...acc, ref.referenceNumber];
    } else {
      return acc;
    }
  }, []);
};
// where sellerNumber is supported, we need both referenceNumber and sellerNumber
export const collectReferenceKeys = (references: ReferenceLocal[]): ReferenceKey[] => {
  return references.reduce((acc: ReferenceKey[], ref: ReferenceLocal) => {
    if (ref.isSelected && hasValidReferenceNumber(ref.referenceNumber)) {
      return [...acc, { referenceNumber: ref.referenceNumber, sellerNumber: ref.sellerNumber }];
    } else {
      return acc;
    }
  }, []);
};

export const collectReferenceNumbersWithDelivery = (references: ReferenceLocal[]): ReferenceWithDelivery[] => {
  return references.reduce((acc: ReferenceWithDelivery[], ref: ReferenceLocal) => {
    if (ref.isSelected && hasValidReferenceNumber(ref.referenceNumber)) {
      return [
        ...acc,
        {
          reference: ref.referenceNumber,
          deliveryType: ref.deliveryType,
          sellerNumber: ref.sellerNumber,
        } as ReferenceWithDelivery,
      ];
    } else {
      return acc;
    }
  }, []);
};

export const collectAllReferences = (
  references: ReferenceLocal[] | undefined,
  _disabledRefs?: string[],
): ReferenceKey[] => {
  if (!references) {
    return [];
  }
  return references.map((ref) => ({
    referenceNumber: ref.referenceNumber,
    sellerNumber: ref.sellerNumber,
  }));
};

export function filterSelectedActiveReferences(
  references: ReferenceLocal[] | undefined,
  disabledRefs?: string[],
): ReferenceLocal[] {
  if (!references) {
    return [];
  }

  return references.filter((ref) => ref.isSelected && !disabledRefs?.includes(ref.referenceNumber));
}

export function countSelectedActiveReferences(
  references: ReferenceLocal[] | undefined,
  disabledRefs?: string[],
): number {
  return filterSelectedActiveReferences(references, disabledRefs).length;
}

export const countIsSelectedReferences = (references: ReferenceLocal[] | undefined): number => {
  if (!references) {
    return 0;
  }
  return references.reduce((num: number, ref: ReferenceLocal) => (ref.isSelected ? num + 1 : num), 0);
};

export const getAscFilterArray = (
  filters: Map<AscOrderFilterAndSortFieldLocal, string>,
): FilterBy<FilterAndSortField>[] => {
  return Array.from(filters.entries()).map(([field, value]) => {
    switch (field) {
      case DATE_FROM:
        return new FilterBy<FilterAndSortField>(
          AscOrdersFilterAndSortField.DATE,
          value,
          FilterOperator.GREATER_THAN_OR_EQUAL,
        );
      case DATE_TO:
        return new FilterBy<FilterAndSortField>(
          AscOrdersFilterAndSortField.DATE,
          value,
          FilterOperator.LESS_THAN_OR_EQUAL,
        );
      case AscOrdersFilterAndSortField.STATUS:
        return new FilterBy<FilterAndSortField>(field, value, FilterOperator.EQUAL);
      default:
        return new FilterBy<FilterAndSortField>(field, value, FilterOperator.CONTAINS);
    }
  });
};

export const getClaimsFilterArray = (
  filters: Map<ClaimsFilterAndSortFieldLocal, string | string[]>,
): (FilterBy<ClaimsFilterAndSortField> | FilterArray<ClaimsFilterAndSortField>)[] => {
  return Array.from(filters.entries()).map(([field, value]) => {
    switch (field) {
      case DATE_FROM:
        return new FilterBy<ClaimsFilterAndSortField>(
          ClaimsFilterFields.CREATED_AT,
          value as string,
          FilterOperator.GREATER_THAN_OR_EQUAL,
        );
      case DATE_TO:
        return new FilterBy<ClaimsFilterAndSortField>(
          ClaimsFilterFields.CREATED_AT,
          value as string,
          FilterOperator.LESS_THAN_OR_EQUAL,
        );
      default: {
        if (Array.isArray(value)) {
          return new FilterArray<ClaimsFilterAndSortField>(
            'OR',
            value.map((valueOption) => new FilterBy<ClaimsFilterAndSortField>(field, valueOption, 'CONTAINS')),
          );
        } else {
          return new FilterBy<ClaimsFilterAndSortField>(field, value, 'CONTAINS');
        }
      }
    }
  });
};

export const mapClaim = (claimForm: ClaimForm): CreateOrderClaimRequest => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { referenceNumberOrdered, referenceNumberReceived, quantityReceived, quantityClaimed, ...rest } = claimForm;

  const isWrongPart = rest.reason === 'Y05';
  const isFullOTC = getFormGroupType(rest.formType) === FULL_OTC;
  const isY9G = rest.reason === 'Y9G';

  return {
    ...rest,
    labour: isY9G ? mapLabour(rest.labour) : undefined,
    vehicle: isY9G ? rest.vehicle : undefined,
    symptom: isY9G ? rest.symptom : undefined,
    referenceNumber: isWrongPart && referenceNumberReceived ? referenceNumberReceived : referenceNumberOrdered,
    quantity: isWrongPart && quantityReceived ? quantityReceived : quantityClaimed,
    quantityReceived: isFullOTC ? quantityReceived : undefined,
  };
};

export const buildExadisReturnClaimItems = (lines: ExadisReturnListLine[]): ClaimItem[] =>
  lines.map((line) => ({
    invoiceNumber: line.invoiceNumber,
    invoiceLineNumber: line.invoiceLineNumber,
    referenceNumber: line.reference,
    quantityInvoiced: line.requestedQuantity,
    quantityClaimed: line.requestedQuantity,
  }));

export const buildDepositReturnClaimItems = (lines: DepositReturnListLine[]): ClaimItem[] =>
  buildExadisReturnClaimItems(lines);

const mapLabour = (labour: ClaimLabourInfo | undefined): ClaimLabourInfo | undefined => {
  if (!labour) {
    return undefined;
  }
  // When isFitted is false, only send the flag — omit detail fields that were hidden in the UI
  return labour.isFitted ? labour : { isFitted: false as const };
};

export const mapAdditionalClaim = (claimForm: ClaimForm, linkedClaimId?: string): CreateOrderClaimRequest => {
  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    contact,
    email,
    option,
    phoneNumber,
    pickupAddress,
    pickupTimeSlots,
    referenceNumberOrdered,
    referenceNumberReceived,
    quantityOrdered,
    quantityReceived,
    quantityClaimed,
    attachments,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...rest
  } = claimForm;
  return {
    ...rest,
    reason: 'Y02',
    referenceNumber: referenceNumberOrdered,
    quantity: quantityClaimed,
    linkedClaimId,
  };
};

export const getMyClientsFilterArray = (
  filters: Map<MyClientsOrderFilterAndSortFieldLocal, string>,
): FilterBy<MyClientsOrderFilterAndSortField>[] => {
  return Array.from(filters.entries()).map(([field, value]) => {
    switch (field) {
      case DATE_FROM:
        return new FilterBy<MyClientsOrderFilterAndSortField>(
          MyClientsOrdersFilterAndSortField.ORDER_DATE,
          value,
          FilterOperator.GREATER_THAN_OR_EQUAL,
        );
      case DATE_TO:
        return new FilterBy<MyClientsOrderFilterAndSortField>(
          MyClientsOrdersFilterAndSortField.ORDER_DATE,
          value,
          FilterOperator.LESS_THAN_OR_EQUAL,
        );
      case MyClientsOrdersFilterAndSortField.STATUS:
        return new FilterBy<MyClientsOrderFilterAndSortField>(field, value, FilterOperator.IN);
      case MyClientsOrdersFilterAndSortField.DMS_ACKNOWLEDGED:
      case MyClientsOrdersFilterAndSortField.URGENT:
      case MyClientsOrdersFilterAndSortField.SUPPLIER_NAME:
        return new FilterBy<MyClientsOrderFilterAndSortField>(field, value, FilterOperator.EQUAL);
      default:
        return new FilterBy<MyClientsOrderFilterAndSortField>(field, value, FilterOperator.CONTAINS);
    }
  });
};
